import React from 'react';
import { withRouter } from 'react-router-dom';
import * as Sentry from '@sentry/react';
import { Environment } from 'src/Environment';
import { v4 } from 'uuid';
import { OnboardingFormBackground } from '../onboarding-form-background/onboarding-form-background';
import { ShapesDecorativeBorder, Tooltip } from 'src/components';
import { RETRY_ATTEMPT } from 'src/common/constants';
import TealiumEvents from 'src/helpers/tealium-events';
import MiniAppEvents from 'src/helpers/miniapp-events';
import { CopyToClipboard, Error as TechnicalErrorIcon } from 'src/assets/images';
import { TechnicalErrorProvider } from 'src/context/technical-error-context';
import { tapOnPhoneSessionKey, vfsHomeUrl } from 'src/common';
import { getJourneyNameFromSessionStorage } from '../../../../utils/SessionStorage/GetJourneyNameFromSessionStorage';
import { SENTRY_TAG_KEYS } from 'src/types';
import './technical-error.scss';

type State = {
  hasError: boolean;
  refreshCount: number;
  showButton: boolean;
  uuid?: string;
  isTopJourney: boolean;
};

export class TechnicalError extends React.Component<{}, State> {
  UNHANDLED_REJECTION_EVENT_NAME = 'unhandledrejection';
  state: State;

  constructor(props) {
    super(props);
    this.state = {
      hasError: false,
      refreshCount: parseInt(sessionStorage.getItem('refreshCount') || '0') || 0,
      showButton: true,
      isTopJourney: !!sessionStorage.getItem(tapOnPhoneSessionKey),
    };
  }

  handleRefresh = () => {
    const { refreshCount } = this.state;
    if (refreshCount < RETRY_ATTEMPT) {
      sessionStorage.setItem('refreshCount', `${refreshCount + 1}`);
      this.setState({ refreshCount: refreshCount + 1 });
      if (refreshCount + 1 === RETRY_ATTEMPT) {
        this.forceUpdate();
      } else {
        window.location.reload();
      }
    }
  };

  static getDerivedStateFromError(error) {
    const uuid = v4().replace(/-/g, '');
    const isTop = !!sessionStorage.getItem(tapOnPhoneSessionKey);

    TealiumEvents.onTechnicalErrorPageLoadIssue('error occurred during page render', isTop);
    if (!Environment?.current?.disabledFeatures?.sentry) {
      Sentry.captureMessage(`${uuid}: ${error.message}`, 'error');
      Sentry.setTag(SENTRY_TAG_KEYS.JOURNEY_NAME, getJourneyNameFromSessionStorage());
    }

    return { hasError: true, uuid };
  }

  redirectToTarget = () => {
    window.location.href = vfsHomeUrl;
  };

  componentDidMount() {
    window.addEventListener(this.UNHANDLED_REJECTION_EVENT_NAME, this.onUnhandledRejection);
  }

  componentWillUnmount() {
    window.removeEventListener(this.UNHANDLED_REJECTION_EVENT_NAME, this.onUnhandledRejection);
  }

  onUnhandledRejection = (event) => {
    event.promise.catch((error) => {
      this.setState(TechnicalError.getDerivedStateFromError(error));
    });
  };

  /**
   * Used to determine the type of tealium event that is logged.
   * @param {string} errorMessage - The tealium error message to send
   * @param {boolean} isEvent - (default = false) Determines if the error happened on page load or due to an event like an API call
   * @param {boolean} suppressTealiumEvent  - (default = false) Send an event or not
   */
  handleTechnicalError = (errorMessage, isEvent = false, suppressTealiumEvent = false) => {
    if (!suppressTealiumEvent) {
      if (isEvent) {
        TealiumEvents.onTechnicalErrorUserEventTriggeredIssue(errorMessage);
      } else {
        TealiumEvents.onTechnicalErrorPageLoadIssue(errorMessage, this.state.isTopJourney);
      }
    }

    const uuid = v4().replace(/-/g, '');
    this.setState({ hasError: true, uuid: uuid });

    const error = new Error(errorMessage);

    if (!Environment?.current?.disabledFeatures?.sentry) {
      Sentry.captureMessage(`${uuid}: ${error.message}`, 'fatal');
      Sentry.setTag(SENTRY_TAG_KEYS.JOURNEY_NAME, getJourneyNameFromSessionStorage());
    }

    // exit execution from which this function was called
    throw error;
  };

  copyUuidToClipboard = () => navigator.clipboard.writeText(this.state?.uuid ?? '');

  formContent() {
    const { refreshCount } = this.state;
    return (
      <div className="form">
        <section className="technical-error-form">
          <div className="background-container">
            <div className="top-background-container">
              <div className="columns is-gapless is-centered is-mobile">
                <div className="column is-four-fifths">
                  <div className="has-text-centered technical-error-content">
                    <div className="technical-error-icon">
                      <img src={TechnicalErrorIcon} />
                    </div>

                    <p className="heading-four technical-error-heading">Technical issue</p>

                    <div className="uuid-block">
                      <p>{this.state.uuid}</p>

                      {/* @ts-ignore */}
                      <Tooltip content="Copied!" showOnClick copyToClipboard>
                        <img src={CopyToClipboard} alt="Copy" onClick={this.copyUuidToClipboard} />
                      </Tooltip>
                    </div>

                    <p className="badge-small-regular technical-error-text">
                      Uh oh! We’re experiencing technical difficulties, but we’re working on it.
                      <br />
                      Please try again later.
                    </p>
                  </div>
                </div>
              </div>
            </div>
            <div className="columns is-mobile is-centered">
              <div className="column is-four-fifths">
                <div className="columns is-mobile">
                  <div className="column is-full">
                    <div className="technical-error-button has-text-centered">
                      {this.state.isTopJourney ? (
                        <p className="badge-small-regular technical-error-text">
                          Please close the app and try again later.
                        </p>
                      ) : refreshCount < 3 ? (
                        <button
                          id="refreshButton"
                          className="button is-primary fullwidth is-rounded button-text"
                          automationid="Onboarding_Technical_Error_Button_Refresh_Page"
                          onClick={this.handleRefresh}
                        >
                          Try Again
                        </button>
                      ) : (
                        <button
                          className="button is-primary fullwidth is-rounded button-text"
                          automationid="Onboarding_Technical_Error_Button_Return_To_Home_Page"
                          onClick={() => {
                            if (MiniAppEvents.isInMiniApp()) {
                              MiniAppEvents.sendMessage({
                                messageType: 'end_of_journey',
                              });
                            } else {
                              this.redirectToTarget();
                            }
                          }}
                        >
                          Return to home page
                        </button>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </section>
      </div>
    );
  }

  render() {
    if (this.state.hasError) {
      return (
        <div className="technical-error">
          <TechnicalErrorProvider
            handleTechnicalError={(errorMessage, isEvent, suppressTealiumEvent) =>
              this.handleTechnicalError(errorMessage, isEvent, suppressTealiumEvent)
            }
          >
            {/* @ts-ignore */}
            <OnboardingFormBackground>
              <ShapesDecorativeBorder>{this.formContent()}</ShapesDecorativeBorder>
            </OnboardingFormBackground>
          </TechnicalErrorProvider>
        </div>
      );
    }

    return (
      <TechnicalErrorProvider
        handleTechnicalError={(errorMessage, isEvent, suppressTealiumEvent) =>
          this.handleTechnicalError(errorMessage, isEvent, suppressTealiumEvent)
        }
      >
        {this.props.children}
      </TechnicalErrorProvider>
    );
  }
}

export default withRouter(TechnicalError);
